/**
 * Página do site dedicada a um segmento (rota /para/:segmento)
 *
 * Fala direto com o dono de cada tipo de negócio: dores específicas, como o
 * sistema resolve, passo a passo (agenda/encomendas), planos e dúvidas.
 * O cadastro já chega com o segmento pré-selecionado. Útil para anúncios e
 * buscas como "sistema para pizzaria" ou "agenda online para barbearia".
 *
 * @author Equipe VendeAi
 * @since 1.2.0
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, Check, CheckCircle2, X } from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { BRAND, FALLBACK_PLANS } from './content';
import { faqForPage } from './seo';
import { findSegmentBySlug, SEGMENT_PAGES } from './segments';
import { usePageMeta } from './usePageMeta';
import { usePlatformData } from './usePlatformData';
import { useHashScroll } from './useHashScroll';
import { SiteHeader } from './components/SiteHeader';
import { PhoneMockup, IntegrationsStrip } from './components/Hero';
import { Reveal, SectionHeading } from './components/Reveal';
import { SavingsCalculator } from './components/SegmentsAndSavings';
import { Pricing } from './components/Pricing';
import { Testimonials } from './components/Testimonials';
import {
  Faq,
  ContactSection,
  FinalCta,
  SiteFooter,
  WhatsAppFloat,
  MobileStickyCta,
} from './components/FaqContact';
import './website.css';

export function SegmentLanding() {
  const { segmento } = useParams<{ segmento: string }>();
  const segment = findSegmentBySlug(segmento);

  if (!segment) return <Navigate to="/" replace />;

  // "key" recria a página ao trocar de segmento pelo menu (estado e rolagem limpos)
  return <SegmentLandingContent key={segment.slug} slug={segment.slug} />;
}

function SegmentLandingContent({ slug }: { slug: string }) {
  const segment = findSegmentBySlug(slug) ?? SEGMENT_PAGES[0];
  const { isAuthenticated } = useAuth();
  const { plans, plansLoading, platform, testimonials, trialDays } = usePlatformData();
  const [selectedPlan, setSelectedPlan] = useState('');

  usePageMeta(
    segment?.seoTitle ?? BRAND,
    segment?.seoDescription ?? '',
    `/para/${segment?.slug ?? ''}`,
  );
  useHashScroll();

  const faqItems = useMemo(() => (segment ? faqForPage({ segment }) : []), [segment]);

  // Depoimentos do mesmo segmento primeiro
  const orderedTestimonials = useMemo(() => {
    if (!segment) return testimonials;
    const label = segment.label.toLowerCase();
    return [...testimonials].sort((a, b) => {
      const aMatch = (a.segment ?? '').toLowerCase().includes(label) ? 0 : 1;
      const bMatch = (b.segment ?? '').toLowerCase().includes(label) ? 0 : 1;
      return aMatch - bMatch;
    });
  }, [testimonials, segment]);

  const handleTalkToSales = useCallback((planSlug: string) => {
    setSelectedPlan(planSlug);
    document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (!segment) return null;

  const signupHref = `/cadastro?segmento=${segment.type}`;

  return (
    <div className="vw-root min-h-screen">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-slate-900"
      >
        Pular para o conteúdo
      </a>
      <SiteHeader isAuthenticated={isAuthenticated} basePath="/" signupHref={signupHref} />
      <main id="conteudo">
        {/* Hero do segmento */}
        <section
          id="inicio"
          className="relative isolate overflow-hidden pb-20 pt-28 sm:pt-36 lg:pb-24 lg:pt-40"
        >
          <div className="vw-grid absolute inset-0 -z-10" aria-hidden />
          <div
            className="vw-orb -z-10 left-[-10%] top-[-10%] h-[420px] w-[420px] bg-violet-700"
            aria-hidden
          />
          <div
            className="vw-orb -z-10 right-[-8%] top-[20%] h-[380px] w-[380px] bg-fuchsia-700/70"
            aria-hidden
          />
          <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8">
            <div className="text-center lg:text-left">
              <span className="vw-gradient-border inline-flex items-center gap-2 rounded-full bg-slate-900/70 px-4 py-1.5 text-xs font-medium text-violet-200 sm:text-sm">
                <segment.icon size={14} className="text-fuchsia-300" aria-hidden />
                {segment.heroEyebrow}
              </span>
              <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl xl:text-6xl">
                {segment.heroTitle}{' '}
                <span className="vw-gradient-text">{segment.heroHighlight}</span>
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg lg:mx-0">
                {segment.heroSubtitle}
              </p>
              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
                <Link
                  to={signupHref}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-600 bg-[length:200%_auto] px-6 py-4 text-base font-semibold text-white shadow-xl shadow-violet-900/50 transition-all hover:bg-right sm:w-auto sm:whitespace-nowrap"
                >
                  Começar teste grátis de {trialDays} dias
                  <ArrowRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Link>
                <a
                  href="#como-resolve"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-6 py-4 text-base font-semibold text-white backdrop-blur transition hover:bg-white/10 sm:w-auto sm:whitespace-nowrap"
                >
                  Ver como funciona
                </a>
              </div>
              <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-slate-400 lg:justify-start">
                {['Sem cartão de crédito', 'Sem fidelidade', 'Sem comissão por pedido'].map(t => (
                  <li key={t} className="flex items-center gap-1.5">
                    <Check size={16} className="text-emerald-400" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative mx-auto flex w-full max-w-lg justify-center lg:justify-end">
              <div className="vw-float relative z-10">
                <PhoneMockup mockup={segment.mockup} />
              </div>
            </div>
          </div>
          <IntegrationsStrip />
        </section>

        {/* Dores */}
        <section id="dores" className="relative scroll-mt-20 py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
              eyebrow="Você se reconhece?"
              title={
                <>
                  Os problemas que mais custam caro para{' '}
                  <span className="vw-gradient-text">{segment.audience}</span>
                </>
              }
            />
            <ul className="mt-12 grid gap-4 sm:grid-cols-2">
              {segment.pains.map((pain, i) => (
                <Reveal
                  as="li"
                  key={pain.title}
                  delay={i * 60}
                  className="flex gap-4 rounded-3xl border border-rose-500/20 bg-rose-500/[0.04] p-6"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-300">
                    <X size={16} aria-hidden />
                  </span>
                  <div>
                    <h3 className="font-semibold text-white">{pain.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                      {pain.description}
                    </p>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>

        {/* Soluções */}
        <section id="como-resolve" className="relative scroll-mt-20 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
              eyebrow={`Como o ${BRAND} resolve`}
              title={
                <>
                  Tudo o que {segment.audience.split(',')[0]} precisam,{' '}
                  <span className="vw-gradient-text">em um só sistema</span>
                </>
              }
            />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {segment.solutions.map((item, i) => (
                <Reveal
                  key={item.title}
                  delay={(i % 3) * 60}
                  className="group rounded-3xl border border-white/10 bg-white/[0.03] p-7 transition-all hover:-translate-y-1 hover:border-violet-400/40 hover:bg-violet-500/[0.06]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-500/15 text-violet-200">
                    <item.icon size={22} aria-hidden />
                  </span>
                  <h3 className="mt-5 font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.description}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Passo a passo (agenda e encomendas) */}
        {segment.howItWorks && (
          <section className="relative py-20 sm:py-24">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <SectionHeading
                eyebrow="Na prática"
                title={
                  <>
                    Como funciona <span className="vw-gradient-text">no dia a dia</span>
                  </>
                }
              />
              <ol className="mt-12 grid gap-6 md:grid-cols-3">
                {segment.howItWorks.map((step, i) => (
                  <Reveal
                    as="li"
                    key={step.title}
                    delay={i * 80}
                    className="vw-gradient-border rounded-3xl bg-gradient-to-br from-violet-950/60 to-slate-900/70 p-7"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-lg font-bold text-white">
                      {i + 1}
                    </span>
                    <h3 className="mt-5 font-semibold text-white">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">
                      {step.description}
                    </p>
                  </Reveal>
                ))}
              </ol>
            </div>
          </section>
        )}

        {segment.showSavings && (
          <SavingsCalculator plans={plans.length > 0 ? plans : FALLBACK_PLANS} />
        )}

        <Testimonials items={orderedTestimonials} />

        <Pricing
          plans={plans}
          loading={plansLoading}
          onTalkToSales={handleTalkToSales}
          segmentType={segment.type}
        />

        {/* Outros segmentos */}
        <section className="relative py-16">
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
            <p className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-300">
              <CheckCircle2 size={16} className="text-emerald-300" aria-hidden /> O {BRAND} também
              atende
            </p>
            <ul className="mt-5 flex flex-wrap justify-center gap-2">
              {SEGMENT_PAGES.filter(s => s.slug !== segment.slug).map(s => (
                <li key={s.slug}>
                  <Link
                    to={`/para/${s.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-300 transition hover:border-violet-400/40 hover:text-white"
                  >
                    <s.icon size={15} className="text-violet-300" aria-hidden /> {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Faq
          items={faqItems}
          title={`Dúvidas sobre o ${BRAND} para ${segment.label.toLowerCase()}`}
        />
        <ContactSection plans={plans} selectedPlan={selectedPlan} platform={platform} />
        <FinalCta trialDays={trialDays} signupHref={signupHref} />
      </main>
      <SiteFooter platform={platform} basePath="/" />
      <WhatsAppFloat number={platform?.supportWhatsApp} />
      <MobileStickyCta href={signupHref} label={`Testar grátis por ${trialDays} dias`} />
    </div>
  );
}
